import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Sun, Moon, LogOut, User, Bell, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';

import { API_BASE_URL as API } from '../utils/apiConfig';
const FUNDLE_LOGO = 'https://customer-assets.emergentagent.com/job_fundle-ai/artifacts/tu7vfzht_erj2rwul_fundle_logo-white-min-382x100.png';

export default function Header({ onMenuClick }) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, logout, isSuperAdmin } = useAuth();
  const { selectedBrand } = useChat();
  const [alerts, setAlerts] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(false);

  // Fetch sync alerts for super admins
  const fetchAlerts = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const response = await fetch(`${API}/api/settings/sync-alerts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchAlerts();
    // Refresh alerts every 60 seconds
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const dismissAlert = async (brandId, alertType) => {
    try {
      await fetch(`${API}/api/settings/sync-alerts/dismiss?brand_id=${brandId}&alert_type=${alertType}`, {
        method: 'POST',
        credentials: 'include'
      });
      setAlerts(prev => prev.filter(a => !(a.brand_id === brandId && a.type === alertType)));
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;
  const totalAlertCount = criticalCount + warningCount;

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  return (
    <header 
      className="h-14 border-b border-border/50 bg-background/60 backdrop-blur-lg sticky top-0 z-40 flex items-center justify-between px-4"
      data-testid="header"
    >
      {/* Left side */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onMenuClick}
          data-testid="menu-btn"
        >
          <Menu className="w-5 h-5" />
        </Button>
        
        <img 
          src={FUNDLE_LOGO} 
          alt="Fundle" 
          className="h-6 hidden sm:block cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => {
            if (isSuperAdmin) {
              // Clear selected brand to go back to landing page
              window.location.href = '/';
            }
          }}
          title={isSuperAdmin ? "Go to Home" : "Fundle Brain"}
        />
        
        {selectedBrand && (
          <span className="text-sm text-muted-foreground hidden md:block">
            / {selectedBrand.name}
          </span>
        )}
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* Sync Alerts for Super Admin */}
        {isSuperAdmin && (
          <Popover open={alertsOpen} onOpenChange={setAlertsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                data-testid="sync-alerts-btn"
              >
                <Bell className="w-5 h-5" />
                {totalAlertCount > 0 && (
                  <span className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                    criticalCount > 0 ? 'bg-red-500' : 'bg-yellow-500'
                  }`}>
                    {totalAlertCount > 9 ? '9+' : totalAlertCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              <div className="p-3 border-b border-border">
                <h4 className="font-semibold">Sync Alerts</h4>
                <p className="text-xs text-muted-foreground">
                  {totalAlertCount === 0 ? 'No active alerts' : `${totalAlertCount} alert${totalAlertCount !== 1 ? 's' : ''}`}
                </p>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {alerts.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    <p className="text-sm">All systems healthy</p>
                  </div>
                ) : (
                  alerts.slice(0, 10).map((alert, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 border-b border-border last:border-0 ${
                        alert.severity === 'critical' ? 'bg-red-500/10' :
                        alert.severity === 'warning' ? 'bg-yellow-500/10' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {alert.severity === 'critical' && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />}
                        {alert.severity === 'warning' && <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />}
                        {alert.severity === 'info' && <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{alert.message}</p>
                          <p className="text-xs text-muted-foreground truncate">{alert.details}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 flex-shrink-0"
                          onClick={() => dismissAlert(alert.brand_id, alert.type)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {alerts.length > 0 && (
                <div className="p-2 border-t border-border">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full text-xs"
                    onClick={() => { setAlertsOpen(false); window.location.href = '/dashboard/data-sync-history'; }}
                  >
                    View Data Sync Dashboard
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          data-testid="theme-toggle"
        >
          {theme === 'dark' ? (
            <Sun className="w-5 h-5" />
          ) : (
            <Moon className="w-5 h-5" />
          )}
        </Button>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback>
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <div className="flex items-center gap-2 p-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback>
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer" data-testid="profile-btn" onClick={() => navigate('/profile')}>
              <User className="w-4 h-4 mr-2" />
              Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="cursor-pointer text-destructive focus:text-destructive" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
